import React from 'react';
import { motion } from 'framer-motion';
import { 
  Coins, 
  ArrowRight, 
  ArrowLeftRight,
  Network,
  Droplets,
  Globe,
  Shield,
  Layers,
  Zap,
  Users,
  Lock,
  Wallet
} from 'lucide-react';

const FlowStep = ({ icon: Icon, label, color, isLast }) => (
  <div className="flex items-center">
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      className={`flex flex-col items-center gap-2`}
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
        <Icon className="w-8 h-8 text-white" />
      </div>
      <span className="text-xs text-slate-400 text-center max-w-[80px]">{label}</span>
    </motion.div>
    {!isLast && (
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mx-3"
      >
        <ArrowRight className="w-6 h-6 text-slate-500" />
      </motion.div>
    )}
  </div>
);

const BiDirectionalFlow = ({ leftIcon: LeftIcon, leftLabel, rightIcon: RightIcon, rightLabel, middleIcon: MiddleIcon, middleLabel, leftColor, rightColor, middleColor }) => (
  <div className="flex items-center justify-center gap-2">
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="flex flex-col items-center gap-2"
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${leftColor} flex items-center justify-center shadow-lg`}>
        <LeftIcon className="w-8 h-8 text-white" />
      </div>
      <span className="text-xs text-slate-400 text-center max-w-[80px]">{leftLabel}</span>
    </motion.div>

    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.1 }}
      className="mx-2"
    >
      <ArrowLeftRight className="w-6 h-6 text-slate-500" />
    </motion.div>

    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="flex flex-col items-center gap-2"
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${middleColor} flex items-center justify-center shadow-lg`}>
        <MiddleIcon className="w-8 h-8 text-white" />
      </div>
      <span className="text-xs text-slate-400 text-center max-w-[80px]">{middleLabel}</span>
    </motion.div>

    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.3 }}
      className="mx-2"
    >
      <ArrowLeftRight className="w-6 h-6 text-slate-500" />
    </motion.div>

    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="flex flex-col items-center gap-2"
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${rightColor} flex items-center justify-center shadow-lg`}>
        <RightIcon className="w-8 h-8 text-white" />
      </div>
      <span className="text-xs text-slate-400 text-center max-w-[80px]">{rightLabel}</span>
    </motion.div>
  </div>
);

const CurrencyFlowDiagram = ({ currencyType, currencyData }) => {
  const renderFlow = () => {
    switch (currencyType?.id) {
      case 'token':
        return (
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-sm font-medium text-slate-300">How Simple Tokens Work</h4>
            <div className="flex items-center">
              <FlowStep 
                icon={Users} 
                label="Identity Owner" 
                color="from-blue-500 to-indigo-600"
              />
              <FlowStep 
                icon={Coins} 
                label="Token Creation" 
                color="from-indigo-500 to-purple-600"
              />
              <FlowStep 
                icon={Wallet} 
                label="Preallocations" 
                color="from-purple-500 to-pink-600"
              />
              <FlowStep 
                icon={currencyData?.isMintable ? Zap : Lock} 
                label={currencyData?.isMintable ? "Mintable Supply" : "Fixed Supply"} 
                color={currencyData?.isMintable ? "from-amber-500 to-orange-600" : "from-emerald-500 to-teal-600"}
                isLast
              />
            </div>
            <p className="text-xs text-slate-500 max-w-md text-center">
              {currencyData?.isMintable 
                ? "Centralized token: ID controller can mint and burn tokens at any time"
                : "Decentralized token: Supply is permanently fixed at launch, no minting possible"
              }
            </p>
          </div>
        );

      case 'basket':
        return (
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-sm font-medium text-slate-300">How Liquidity Pools Work</h4>
            <div className="flex items-center justify-center flex-wrap gap-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
                      <Coins className="w-5 h-5 text-white" />
                    </div>
                  ))}
                </div>
                <span className="text-xs text-slate-400">Reserve Currencies</span>
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <ArrowLeftRight className="w-8 h-8 text-emerald-500" />
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                  <Droplets className="w-10 h-10 text-white" />
                </div>
                <span className="text-xs text-slate-400">Basket Currency</span>
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                <ArrowLeftRight className="w-8 h-8 text-emerald-500" />
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <span className="text-xs text-slate-400">Traders</span>
              </motion.div>
            </div>
            <p className="text-xs text-slate-500 max-w-md text-center">
              Automated Market Maker: Users can convert between any reserve currency through the basket with automatic price discovery
            </p>
          </div>
        );

      case 'pbaas':
        return (
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-sm font-medium text-slate-300">How PBaaS Chains Work</h4>
            <div className="flex items-center">
              <FlowStep 
                icon={Network} 
                label="New Blockchain" 
                color="from-purple-500 to-pink-600"
              />
              <FlowStep 
                icon={Shield} 
                label="Notarization" 
                color="from-pink-500 to-rose-600"
              />
              <FlowStep 
                icon={Layers} 
                label="Bridge Converter" 
                color="from-indigo-500 to-purple-600"
              />
              <FlowStep 
                icon={Globe} 
                label="Verus Network" 
                color="from-blue-500 to-indigo-600"
                isLast
              />
            </div>
            <p className="text-xs text-slate-500 max-w-md text-center">
              Independent blockchain with its own miners/stakers, connected to Verus via notarization for cross-chain transfers
            </p>
          </div>
        );

      case 'erc20':
        return (
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-sm font-medium text-slate-300">How Mapped ERC-20 Tokens Work</h4>
            <BiDirectionalFlow
              leftIcon={Coins}
              leftLabel="Token on Verus"
              leftColor="from-blue-500 to-indigo-600"
              middleIcon={Layers}
              middleLabel="vETH Gateway & Bridge"
              middleColor="from-purple-500 to-pink-600"
              rightIcon={Globe}
              rightLabel="ERC-20 on Ethereum"
              rightColor="from-orange-500 to-red-600"
            />
            <p className="text-xs text-slate-500 max-w-md text-center">
              ERC-20 tokens are locked on Ethereum and minted on Verus. Users can bridge tokens in both directions seamlessly.
            </p>
          </div>
        );

      case 'gateway':
        return (
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-sm font-medium text-slate-300">How Gateways Work</h4>
            <BiDirectionalFlow
              leftIcon={Network}
              leftLabel="Verus Network"
              leftColor="from-blue-500 to-indigo-600"
              middleIcon={Shield}
              middleLabel="Gateway (Notarized)"
              middleColor="from-cyan-500 to-blue-600"
              rightIcon={Globe}
              rightLabel="External Network"
              rightColor="from-purple-500 to-pink-600"
            />
            <p className="text-xs text-slate-500 max-w-md text-center">
              Gateways provide secure, notarized bridges between Verus and external blockchains
            </p>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center">
            <p className="text-sm text-slate-500">Select a currency type to see how it works</p>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-slate-800/80 backdrop-blur-sm border-t border-slate-700/50 p-6"
    >
      {renderFlow()}
    </motion.div>
  );
};

export default CurrencyFlowDiagram;
